/**
 * The jurisdiction step's branching, as ONE pure function (#411).
 *
 * THE RULE (issue #411): every question changes what the learner sees next. If
 * an answer changes nothing on screen, the question does not exist. So the
 * question list is DERIVED from the answers so far plus the backend's record,
 * never a fixed array walked in order:
 *
 *   - the rung options come ONLY from `path.ladder` for that state, so Michigan
 *     (no plain master's rung) can never be offered "Master's", and Virginia
 *     can never be offered the limited license it does not issue;
 *   - clinical intent is not asked when the rung already answers it;
 *   - supervision is asked only of someone who could actually be supervised;
 *   - supervisor verification is asked only when there IS a supervisor.
 *
 * Pure: no React, no network, no clock. `licensing.flow.test.ts` pins each
 * branch by asserting the question list CHANGES with the answer.
 */
import {
  Employment,
  LadderRung,
  LICENSING_PROFILE_FIELDS,
  LicenseRung,
  LicensingAnswers,
  LicensingPath,
  ProcessStage,
  YesNoUnknown,
  YesNoUnsure,
} from './types';

export type LicensingQuestionId =
  | 'jurisdiction'
  | 'licenseRung'
  | 'processStage'
  | 'employment'
  | 'clinicalIntent'
  | 'employerSupervision'
  | 'supervisorVerified';

export interface LicensingOption {
  value: string;
  label: string;
}

export interface LicensingQuestion {
  id: LicensingQuestionId;
  /** The collapsed-row label once answered (.pen: uppercase, muted). */
  label: string;
  /** The active-question prompt (.pen: uppercase, muted, above the cards). */
  prompt: string;
  options: LicensingOption[];
}

// ─────────────────────────────── labels ─────────────────────────────────────

const STAGE_LABELS: Record<ProcessStage, string> = {
  not_applied: "Haven't applied yet",
  waiting: 'Waiting on approval',
  approved: 'Approved to test',
  scheduled: 'Exam scheduled',
  retaking: 'Retaking',
  passed: 'Already passed',
};

const EMPLOYMENT_LABELS: Record<Employment, string> = {
  working_sw: 'Working in social work',
  starting_soon: 'Starting a job soon',
  not_working: 'Not working in social work yet',
};

const YES_NO_UNSURE_LABELS: Record<YesNoUnsure, string> = {
  yes: 'Yes',
  no: 'No',
  unsure: 'Not sure',
};

const YES_NO_UNKNOWN_LABELS: Record<YesNoUnknown, string> = {
  yes: 'Yes',
  no: 'No',
  unknown: "I don't know",
};

/** The plain-English half of a rung name; the state's own abbreviation supplies the rest. */
const RUNG_BASE_LABELS: Record<LicenseRung, string> = {
  bachelors: "Bachelor's",
  masters: "Master's",
  limited: 'Limited license',
  supervisee: 'Supervised hours',
  advanced: 'Advanced',
  clinical: 'Clinical',
};

/**
 * "Master's (LMSW)" per the .pen. When the state's own abbreviation is already a
 * PHRASE rather than an acronym ("Limited LMSW", "Supervisee in Social Work",
 * "LMSW (macro)"), the phrase IS the label: "Supervised hours (Supervisee in
 * Social Work)" reads like a form, not like the thing a person is applying for.
 */
export function rungLabel(entry: LadderRung): string {
  const base = RUNG_BASE_LABELS[entry.rung] ?? entry.rung;
  if (!entry.abbr) return base;
  if (entry.abbr.includes(' ')) return entry.abbr;
  return `${base} (${entry.abbr})`;
}

export function stageLabel(stage: ProcessStage): string {
  return STAGE_LABELS[stage];
}

// ────────────────────────────── branching ───────────────────────────────────

/**
 * Intent the rung already settles. Someone answering for the clinical license,
 * or for the supervised-hours rung that only exists to reach it, has told us
 * they want it; asking again is the question that changes nothing.
 */
export function effectiveClinicalIntent(answers: LicensingAnswers): YesNoUnsure | undefined {
  if (answers.licenseRung === 'clinical' || answers.licenseRung === 'supervisee') return 'yes';
  return answers.clinicalIntent;
}

/**
 * The questions to show, in order, given what has been answered and what the
 * backend says this jurisdiction offers.
 *
 * `path` null (not fetched yet, or a state we hold no data for) stops the list
 * at the state question: we will not ask about a ladder we cannot name.
 */
export function licensingQuestions(
  answers: LicensingAnswers,
  path: LicensingPath | null
): LicensingQuestion[] {
  const questions: LicensingQuestion[] = [
    {
      id: 'jurisdiction',
      label: 'State',
      prompt: 'Where are you getting licensed?',
      options: [], // supplied by the state select, not a card list
    },
  ];

  if (!answers.jurisdiction || !path) return questions;

  if (!path.available) {
    // `rung_not_offered` is NOT `no_data`: we hold this state's rulebook, the
    // learner just named a rung it does not have (Virginia has no limited
    // license). Re-ask from the rungs it DOES offer instead of telling them we
    // have not verified their state, which would be false.
    if (path.reason === 'rung_not_offered' && path.offeredRungs.length > 0) {
      questions.push({
        id: 'licenseRung',
        label: 'License level',
        prompt: 'Which license are you working toward?',
        options: path.offeredRungs.map((rung) => ({
          value: rung,
          label: RUNG_BASE_LABELS[rung] ?? rung,
        })),
      });
    }
    return questions;
  }

  // THE RUNGS ARE THE STATE'S, NOT A FIXED LIST. Michigan has no plain master's
  // rung; offering one would send a learner down a path their board does not have.
  questions.push({
    id: 'licenseRung',
    label: 'License level',
    prompt: 'Which license are you working toward?',
    options: path.ladder.map((entry) => ({ value: entry.rung, label: rungLabel(entry) })),
  });

  if (!answers.licenseRung) return questions;

  questions.push({
    id: 'processStage',
    label: 'Where you are',
    prompt: 'Where are you in the process?',
    options: (Object.keys(STAGE_LABELS) as ProcessStage[]).map((stage) => ({
      value: stage,
      label: STAGE_LABELS[stage],
    })),
  });

  if (!answers.processStage) return questions;

  questions.push({
    id: 'employment',
    label: 'Work',
    prompt: 'Are you working in social work right now?',
    options: (Object.keys(EMPLOYMENT_LABELS) as Employment[]).map((value) => ({
      value,
      label: EMPLOYMENT_LABELS[value],
    })),
  });

  if (!answers.employment) return questions;

  // Only worth asking when the rung has not already answered it.
  const rungAnswersIntent = answers.licenseRung === 'clinical' || answers.licenseRung === 'supervisee';
  if (!rungAnswersIntent) {
    questions.push({
      id: 'clinicalIntent',
      label: 'Clinical license later',
      prompt: 'Do you want a clinical license later?',
      options: (Object.keys(YES_NO_UNSURE_LABELS) as YesNoUnsure[]).map((value) => ({
        value,
        label: YES_NO_UNSURE_LABELS[value],
      })),
    });
    if (!answers.clinicalIntent) return questions;
  }

  // Supervision only matters to someone who wants the clinical license AND has
  // (or is about to have) an employer. "Not working yet" has no employer to ask
  // about, so the answer would change nothing.
  const intent = effectiveClinicalIntent(answers);
  if (intent !== 'yes' || answers.employment === 'not_working') return questions;

  questions.push({
    id: 'employerSupervision',
    label: 'Supervision at work',
    prompt: 'Does your employer provide qualifying supervision?',
    options: (Object.keys(YES_NO_UNSURE_LABELS) as YesNoUnsure[]).map((value) => ({
      value,
      label: YES_NO_UNSURE_LABELS[value],
    })),
  });

  if (answers.employerSupervision !== 'yes') return questions;

  questions.push({
    id: 'supervisorVerified',
    label: 'Supervisor approved',
    prompt: 'Has the board approved your supervisor?',
    options: (Object.keys(YES_NO_UNKNOWN_LABELS) as YesNoUnknown[]).map((value) => ({
      value,
      label: YES_NO_UNKNOWN_LABELS[value],
    })),
  });

  return questions;
}

/** The human label for an answer, for the collapsed rows. */
export function answerLabel(
  question: LicensingQuestion,
  answers: LicensingAnswers,
  stateName: string
): string | undefined {
  if (question.id === 'jurisdiction') return answers.jurisdiction ? stateName : undefined;
  const value = answers[question.id];
  if (!value) return undefined;
  return question.options.find((o) => o.value === value)?.label ?? value;
}

/**
 * The jurisdiction holds a rulebook but not the rung the learner named. Their
 * stored answer is not an answer any more, so the rung question re-opens.
 */
export function rungRejected(path: LicensingPath | null): boolean {
  return !!path && !path.available && path.reason === 'rung_not_offered';
}

/** The first question with no (still-valid) answer, or null when the step is complete. */
export function activeQuestion(
  answers: LicensingAnswers,
  path: LicensingPath | null
): LicensingQuestion | null {
  const rejected = rungRejected(path);
  return (
    licensingQuestions(answers, path).find(
      (q) => !answers[q.id] || (q.id === 'licenseRung' && rejected)
    ) ?? null
  );
}

/**
 * Can the learner continue? A state we have no verified record for is COMPLETE
 * at the state answer: there are no honest follow-ups to ask, and holding them
 * on the screen would be a dead end.
 */
export function isLicensingComplete(answers: LicensingAnswers, path: LicensingPath | null): boolean {
  if (!answers.jurisdiction) return false;
  // No path at all (lookup failed): let them through rather than stranding the
  // signup behind a service we could not reach.
  if (!path) return true;
  // `no_data` is a dead end we own: nothing honest left to ask. `rung_not_offered`
  // is answerable, so it holds the step until a rung this state has is picked.
  if (!path.available) return path.reason === 'no_data';
  return activeQuestion(answers, path) === null;
}

/**
 * Answers to CLEAR when an earlier one changes. Re-picking the state must not
 * leave Virginia's rung attached to Michigan's ladder; re-picking the rung must
 * not leave a clinical-intent answer the new rung does not ask for.
 */
export function clearDownstream(
  answers: LicensingAnswers,
  changed: LicensingQuestionId
): LicensingAnswers {
  const order: LicensingQuestionId[] = [
    'jurisdiction',
    'licenseRung',
    'processStage',
    'employment',
    'clinicalIntent',
    'employerSupervision',
    'supervisorVerified',
  ];
  const from = order.indexOf(changed);
  const next: LicensingAnswers = { ...answers };
  for (const id of order.slice(from + 1)) delete next[id];
  return next;
}

/**
 * The answered fields only, as the payload BOTH writers send (createTrialUser
 * via TrialSignupFlow, updateUserProfile via OnboardingPage).
 *
 * Keyed off `LICENSING_PROFILE_FIELDS` on purpose: that makes the shared
 * constant the thing the write path actually walks, so the drift test guards
 * what is sent and not just a list nobody reads. Empty strings and undefined
 * are dropped, so a cleared answer never reaches the callable as `''`.
 */
export function licensingPayload(answers: LicensingAnswers): LicensingAnswers {
  const out: Record<string, string> = {};
  for (const field of LICENSING_PROFILE_FIELDS) {
    const value = answers[field];
    if (typeof value === 'string' && value.length > 0) out[field] = value;
  }
  return out as LicensingAnswers;
}
